/**
 * Frontend Error Reporter (Proxy through API)
 * Sends error data to the backend API which forwards to Discord.
 * No webhook URL exposed in client-side code.
 *
 * Repo: sortswift-kiosk
 */

const REPO_NAME = 'sortswift-kiosk';
const API_BASE =
  (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) ||
  'https://api.sortswift.com/api';
const REPORT_ENDPOINT = `${API_BASE}/client-error-report`;
const RATE_LIMIT_MS = 60_000;
const DIAGNOSTIC_RATE_LIMIT_MS = 15_000;
const CHECKPOINT_KEY = 'sortswift_kiosk_frontend_checkpoint';
const SESSION_KEY = 'sortswift_kiosk_frontend_session_id';
const CHECKPOINT_DEDUPE_MS = 1500;
const RECOVERABLE_CHECKPOINT_MAX_AGE_MS = 30 * 60_000;

let _config = {
  ignoredErrors: [
    'ResizeObserver loop',
    'AbortError',
    'Script error',
    'Could not establish connection. Receiving end does not exist.',
    // Network / connectivity errors (client-side, not real bugs)
    'Network Error',
    'Failed to fetch',
    'NetworkError',
    'Network request failed',
    'TypeError: cancelled',
    'TypeError: Cancelled',
    // Chunk-loading failures (deploy race / flaky connection)
    'ChunkLoadError',
    'Loading chunk',
    'Load failed',
    // Firefox-specific error for accessing GC'd DOM objects
    "can't access dead object",
    // Chunk loading failures — deploy race / stale cache
    'Failed to load script:',
    // Expected 404s from live-chat polling (user not in chat session)
    'Failed to get global chat',
    // Staging sync API errors — not actionable in production monitoring
    'staging-sync-api.sortswift-services.com',
    // Browser fullscreen API — requires user gesture, not a code bug
    'Fullscreen request denied',
    'Cannot request fullscreen',
    'requestFullscreen',
    // Browser extension noise — DarkReader, Firefox reader, MetaMask/ethereum
    'Can\'t find variable: DarkReader',
    'window.ethereum.selectedAddress',
    'Can\'t find variable: __firefox__',
    'window.__firefox__.reader',
    // Content-script/selector pollution from browser extensions
    /standardSelectors/,
    'contentScriptData.init_ts',
    // Suppression patterns added 2026-04-18
    /Store .* not found/,
    // Invalid-slug 404s from buylist config lookup — frontend already
    // handles this as a "no access" state; the console.error is non-actionable.
    'Store not found',
    '/buylist-config',
    // Browser Permissions API denial (user/policy blocked); not a code bug
    'Permissions check failed',
  ],
  ignoredRoutes: [],
  enabled: true,
};

const _recentErrors = new Map();
const _recentDiagnostics = new Map();
let _installed = false;
let _sessionId = null;
let _lastCheckpointKey = null;
let _lastCheckpointAt = 0;

export function configureErrorReporter(overrides = {}) {
  _config = { ..._config, ...overrides };
}

/**
 * Returns true if every frame in the stack trace originates from a browser
 * extension (chrome-extension://, moz-extension://, safari-extension://, etc.).
 * When that's the case the error has nothing to do with our app code.
 */
function _isExtensionError(stack) {
  const urls = stack.match(/https?:\/\/[^\s)]+|[a-z]+-extension:\/\/[^\s)]+/gi);
  if (!urls || urls.length === 0) return false;
  return urls.every((u) =>
    /^(chrome|moz|safari|safari-web)-extension:\/\//i.test(u),
  );
}

function shouldIgnore(message = '') {
  if (!_config.enabled) return true;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return true;
  }
  // A RegExp entry is tested, never handed to includes(): includes() THROWS on a
  // RegExp, every caller's catch read that as "not sent", and from the 2026-04-18
  // entries on (/standardSelectors/, /Store .* not found/) no kiosk error or
  // diagnostic reached us at all (TKT-2026-108319).
  if (
    _config.ignoredErrors.some((pattern) =>
      pattern instanceof RegExp ? pattern.test(message) : message.includes(pattern)
    )
  ) {
    return true;
  }
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (_config.ignoredRoutes.some((route) => path.startsWith(route))) {
      return true;
    }
  }
  return false;
}

function getBasePayload(kind, extra = {}) {
  return {
    kind,
    repoName: REPO_NAME,
    sessionId: getSessionId(),
    url: typeof window !== 'undefined' ? window.location.href : 'server-side',
    pathname:
      typeof window !== 'undefined' ? window.location.pathname : undefined,
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    visibilityState:
      typeof document !== 'undefined' ? document.visibilityState : undefined,
    env: (typeof process !== 'undefined' && process.env?.NODE_ENV) || 'unknown',
    timestamp: new Date().toISOString(),
    ...extra,
  };
}

function makeSessionId() {
  return `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function getSessionId() {
  if (_sessionId) return _sessionId;
  if (typeof window === 'undefined') {
    _sessionId = makeSessionId();
    return _sessionId;
  }
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) {
      _sessionId = existing;
      return _sessionId;
    }
    _sessionId = makeSessionId();
    window.sessionStorage.setItem(SESSION_KEY, _sessionId);
    return _sessionId;
  } catch (_) {
    _sessionId = makeSessionId();
    return _sessionId;
  }
}

function readCheckpoint() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CHECKPOINT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeCheckpoint(payload) {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(CHECKPOINT_KEY, JSON.stringify(payload));
    return true;
  } catch (_) {
    return false;
  }
}

export function markCrashCheckpoint(stage, details = {}) {
  if (typeof window === 'undefined') return false;

  const now = Date.now();
  const checkpointKey = `${stage}::${JSON.stringify(details)}`;
  if (
    _lastCheckpointKey === checkpointKey &&
    now - _lastCheckpointAt < CHECKPOINT_DEDUPE_MS
  ) {
    return false;
  }

  _lastCheckpointKey = checkpointKey;
  _lastCheckpointAt = now;

  return writeCheckpoint({
    sessionId: getSessionId(),
    stage,
    details,
    timestamp: new Date(now).toISOString(),
    timestampMs: now,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    visibilityState:
      typeof document !== 'undefined' ? document.visibilityState : undefined,
  });
}

export function clearCrashCheckpoint(reason = 'clean_exit') {
  if (typeof window === 'undefined') return false;
  try {
    const current = readCheckpoint();
    if (current && current.sessionId === getSessionId()) {
      window.localStorage.removeItem(CHECKPOINT_KEY);
    }
    _lastCheckpointKey = `cleared::${reason}`;
    _lastCheckpointAt = Date.now();
    return true;
  } catch (_) {
    return false;
  }
}

async function reportRecoveredCrashIfPresent() {
  const checkpoint = readCheckpoint();
  if (!checkpoint) return false;
  if (checkpoint.sessionId === getSessionId()) return false;

  const ageMs = Date.now() - Number(checkpoint.timestampMs || 0);
  if (!Number.isFinite(ageMs) || ageMs < 0 || ageMs > RECOVERABLE_CHECKPOINT_MAX_AGE_MS) {
    clearCrashCheckpoint('stale_checkpoint');
    return false;
  }

  const reported = await postPayload(
    getBasePayload('diagnostic', {
      message: 'Recovered after unexpected frontend termination',
      context: 'Crash recovery checkpoint',
      extra: {
        previousCheckpoint: checkpoint,
        recoveredAt: new Date().toISOString(),
      },
    }),
    { useBeacon: false }
  );

  clearCrashCheckpoint(reported ? 'recovered_reported' : 'recovered_unreported');
  return reported;
}

function pruneRateLimitMap(map, now, ttl) {
  if (map.size <= 100) return;
  for (const [key, ts] of map) {
    if (now - ts > ttl * 5) {
      map.delete(key);
    }
  }
}

async function postPayload(payload, { useBeacon = false } = {}) {
  const body = JSON.stringify(payload);

  if (
    useBeacon &&
    typeof navigator !== 'undefined' &&
    typeof navigator.sendBeacon === 'function'
  ) {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(REPORT_ENDPOINT, blob)) {
        return true;
      }
    } catch (_) {
      // Fall back to fetch below.
    }
  }

  try {
    await fetch(REPORT_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: useBeacon,
    });
    return true;
  } catch (_) {
    return false;
  }
}

export async function reportErrorToDiscord(error, opts = {}) {
  try {
    const message = error?.message || String(error || 'Unknown error');
    if (shouldIgnore(message)) return false;

    const stack = error?.stack || '(no stack trace)';
    if (_isExtensionError(stack)) return false;

    const now = Date.now();
    const dedupeKey = `${opts.context || 'error'}::${message}`;
    if (
      _recentErrors.has(dedupeKey) &&
      now - _recentErrors.get(dedupeKey) < RATE_LIMIT_MS
    ) {
      return false;
    }

    _recentErrors.set(dedupeKey, now);
    pruneRateLimitMap(_recentErrors, now, RATE_LIMIT_MS);

    return postPayload(
      getBasePayload('error', {
        message,
        stack,
        componentStack: opts.componentStack || undefined,
        context: opts.context || undefined,
        extra: opts.extra || undefined,
      }),
      { useBeacon: !!opts.useBeacon }
    );
  } catch (_) {
    return false;
  }
}

export async function reportDiagnosticEvent(name, details = {}, opts = {}) {
  try {
    const message = String(name || 'diagnostic');
    if (shouldIgnore(message)) return false;

    const now = Date.now();
    const dedupeKey = `${message}::${JSON.stringify(details)}`;
    if (
      _recentDiagnostics.has(dedupeKey) &&
      now - _recentDiagnostics.get(dedupeKey) < DIAGNOSTIC_RATE_LIMIT_MS
    ) {
      return false;
    }

    _recentDiagnostics.set(dedupeKey, now);
    pruneRateLimitMap(_recentDiagnostics, now, DIAGNOSTIC_RATE_LIMIT_MS);

    return postPayload(
      getBasePayload('diagnostic', {
        message,
        context: opts.context || undefined,
        extra: details,
      }),
      { useBeacon: !!opts.useBeacon }
    );
  } catch (_) {
    return false;
  }
}

export function installGlobalErrorHandlers() {
  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined' ||
    _installed
  ) {
    return;
  }
  _installed = true;
  getSessionId();
  void reportRecoveredCrashIfPresent();
  markCrashCheckpoint('app_boot', {
    pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });

  window.addEventListener('error', (event) => {
    markCrashCheckpoint('window_error', {
      message: event?.message || undefined,
      filename: event?.filename || undefined,
      line: event?.lineno || undefined,
      column: event?.colno || undefined,
    });
    reportErrorToDiscord(event.error || new Error(event.message), {
      context: `Global error at ${event.filename || 'unknown'}:${event.lineno || 0}:${event.colno || 0}`,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason || 'Unhandled promise rejection'));
    markCrashCheckpoint('unhandled_rejection', {
      message: reason?.message || String(reason),
    });
    reportErrorToDiscord(reason, {
      context: 'Unhandled promise rejection',
    });
  });

  window.addEventListener('pagehide', (event) => {
    clearCrashCheckpoint('pagehide');
    reportDiagnosticEvent(
      'pagehide',
      {
        persisted: typeof event?.persisted === 'boolean' ? event.persisted : undefined,
      },
      { context: 'Window lifecycle', useBeacon: true }
    );
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      markCrashCheckpoint('visibility_visible', {
        pathname: window.location.pathname,
      });
    }
    reportDiagnosticEvent(
      'visibilitychange',
      { visibilityState: document.visibilityState },
      { context: 'Document lifecycle', useBeacon: document.visibilityState === 'hidden' }
    );
  });

  window.addEventListener('beforeunload', () => {
    clearCrashCheckpoint('beforeunload');
  });
}

export default reportErrorToDiscord;
